import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, Classroom, ClassroomStudent } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Users } from "lucide-react";
import { MetricCard } from "../components/comparison/MetricCard";
import { TechniqueComparisonTable } from "../components/comparison/TechniqueComparisonTable";

export default function TeacherComparison() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [selectedClassroomId, setSelectedClassroomId] = useState<string>("");
  const [students, setStudents] = useState<ClassroomStudent[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [studentsData, setStudentsData] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    loadClassrooms();
  }, []);

  useEffect(() => {
    // Parse parameters from URL
    const classroomParam = searchParams.get("classroom");
    const studentsParam = searchParams.get("students");

    if (classroomParam) {
      setSelectedClassroomId(classroomParam);
      loadClassroomStudents(classroomParam);

      if (studentsParam) {
        const ids = studentsParam.split(",").filter(Boolean);
        setSelectedStudentIds(ids);
        if (ids.length >= 2) {
          loadComparison(ids, classroomParam);
        }
      }
    }
  }, [searchParams]);

  const loadClassrooms = async () => {
    try {
      setLoading(true);
      const data = await api.getClassrooms();
      setClassrooms(data);
    } catch (error) {
      console.error("Failed to load classrooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadClassroomStudents = async (classroomId: string) => {
    try {
      setLoadingStudents(true);
      const data = await api.getClassroomStudents(classroomId);
      setStudents(data.filter((s) => s.status === "active"));
    } catch (error) {
      console.error("Failed to load students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadComparison = async (studentIds: string[], classroomId: string) => {
    try {
      setComparing(true);
      const data = await api.getMultipleStudentPerformance(
        studentIds,
        classroomId,
        dateRange,
      );
      setStudentsData(data);
    } catch (error) {
      console.error("Failed to load comparison data:", error);
    } finally {
      setComparing(false);
    }
  };

  const handleClassroomChange = (classroomId: string) => {
    setSelectedClassroomId(classroomId);
    setSelectedStudentIds([]);
    setStudentsData(new Map());
    setSearchParams({ classroom: classroomId });
    loadClassroomStudents(classroomId);
  };

  const toggleStudentSelection = (studentId: string) => {
    const newSelection = selectedStudentIds.includes(studentId)
      ? selectedStudentIds.filter((id) => id !== studentId)
      : [...selectedStudentIds, studentId];

    setSelectedStudentIds(newSelection);

    // Update URL
    if (selectedClassroomId) {
      const params: any = { classroom: selectedClassroomId };
      if (newSelection.length > 0) {
        params.students = newSelection.join(",");
      }
      setSearchParams(params);
    }

    // Load comparison if 2+ selected
    if (newSelection.length >= 2 && selectedClassroomId) {
      loadComparison(newSelection, selectedClassroomId);
    }
  };

  const handleDateRangeChange = (days: number) => {
    setDateRange(days);
    if (selectedStudentIds.length >= 2 && selectedClassroomId) {
      loadComparison(selectedStudentIds, selectedClassroomId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  if (classrooms.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="outline"
          onClick={() => navigate("/teacher")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Teacher Dashboard
        </Button>
        <Alert>
          <Users className="h-4 w-4" />
          <AlertDescription>
            You need to create at least one classroom with students to use the
            comparison feature.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Prepare comparison data
  const comparisonEntities = selectedStudentIds.map((studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    const data = studentsData.get(studentId);

    return {
      id: studentId,
      name: student?.student_info?.username || studentId,
      data: data?.error ? undefined : data?.performance,
      error: data?.error,
      loading: comparing,
    };
  });

  // Prepare metric values
  const getMetricValues = (metricKey: string) => {
    return comparisonEntities
      .filter((e) => e.data && !e.error)
      .map((e) => ({
        entityId: e.id,
        entityName: e.name,
        value: e.data?.[metricKey] ?? 0,
      }));
  };

  // Prepare technique data for comparison table
  const techniqueData = comparisonEntities
    .filter((e) => e.data && !e.error)
    .map((e) => ({
      entityId: e.id,
      entityName: e.name,
      techniques: e.data?.technique_breakdown || [],
    }));

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate("/teacher")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Teacher Dashboard
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Compare Students
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Compare performance metrics across students in your classroom
        </p>
      </div>

      {/* Classroom Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Classroom</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={selectedClassroomId}
            onChange={(e) => handleClassroomChange(e.target.value)}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">-- Select a classroom --</option>
            {classrooms.map((classroom) => (
              <option key={classroom.id} value={classroom.id}>
                {classroom.name} ({classroom.grade_level || "No grade"})
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Student Selection */}
      {selectedClassroomId && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Students to Compare</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStudents ? (
              <div className="text-center py-4 text-gray-600 dark:text-gray-400">
                Loading students...
              </div>
            ) : students.length === 0 ? (
              <Alert>
                <AlertDescription>
                  No active students found in this classroom.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {students.map((student) => (
                    <button
                      key={student.student_id}
                      onClick={() => toggleStudentSelection(student.student_id)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedStudentIds.includes(student.student_id)
                          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            selectedStudentIds.includes(student.student_id)
                              ? "border-indigo-600 bg-indigo-600"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedStudentIds.includes(student.student_id) && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {student.student_info?.username || "Unknown"}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {selectedStudentIds.length > 5 && (
                  <Alert className="mt-4" variant="destructive">
                    <AlertDescription>
                      Maximum 5 students can be compared at once for optimal
                      performance. Please deselect some students.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Date Range Filter */}
      {selectedStudentIds.length >= 2 && selectedStudentIds.length <= 5 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Date Range</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <Button
                  key={days}
                  variant={dateRange === days ? "default" : "outline"}
                  onClick={() => handleDateRangeChange(days)}
                  disabled={comparing}
                >
                  Last {days} days
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison View */}
      {selectedStudentIds.length >= 2 && selectedStudentIds.length <= 5 && (
        <>
          {comparing ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-xl text-gray-600 dark:text-gray-400">
                Loading comparison data...
              </div>
            </div>
          ) : (
            <>
              {/* Key Metrics Comparison */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Performance Metrics
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <MetricCard
                    title="Accuracy"
                    values={getMetricValues("accuracy")}
                    unit="%"
                    highlightBest={true}
                    highlightWorst={true}
                  />
                  <MetricCard
                    title="Problems Solved"
                    values={getMetricValues("problems_solved")}
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Techniques Mastered"
                    values={getMetricValues("techniques_mastered")}
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Assignment Completion Rate"
                    values={comparisonEntities
                      .filter((e) => e.data && !e.error)
                      .map((e) => ({
                        entityId: e.id,
                        entityName: e.name,
                        value: e.data?.assignments_total
                          ? (
                              (e.data.assignments_completed /
                                e.data.assignments_total) *
                              100
                            ).toFixed(1)
                          : "0.0",
                        rawValue: e.data?.assignments_total
                          ? (e.data.assignments_completed /
                              e.data.assignments_total) *
                            100
                          : 0,
                      }))}
                    unit="%"
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Average Assignment Score"
                    values={getMetricValues("assignment_avg_score")}
                    unit="%"
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Current Streak"
                    values={getMetricValues("current_streak")}
                    unit=" days"
                    highlightBest={true}
                  />
                </div>
              </div>

              {/* Technique Comparison Table */}
              {techniqueData.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Technique Breakdown
                  </h2>
                  <TechniqueComparisonTable entities={techniqueData} />
                </div>
              )}

              {/* Error Messages */}
              {comparisonEntities.some((e) => e.error) && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>
                    Some students' data could not be loaded. Please try again
                    later.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
